<script setup lang="ts">
import EducationBlock from '@/pages/lawyer/ui/sections/ExperienceSection/EducationBlock.vue';
import JobsBlock from '@/pages/lawyer/ui/sections/ExperienceSection/JobsBlock.vue';
import ArbitragePracticeBlock from '@/pages/lawyer/ui/sections/ExperienceSection/ArbitragePracticeBlock.vue';
import PublicationsBlock from '@/pages/lawyer/ui/sections/ExperienceSection/PublicationsBlock.vue';
import { LAWYER_INFO } from '@/pages/lawyer/model/keys';
import type { Tab } from '@/shared/ui/AppTabs/model/types/appTabs';

const props = withDefaults(defineProps<{ isCompany?: boolean }>(), {
  isCompany: false,
});

const isMobile: boolean = useIsMobile();
const lawyerInfo = inject(LAWYER_INFO);

const tabList: ComputedRef<Tab[]> = computed(() => {
  const info = lawyerInfo?.value;
  const jobsAmount = info?.workplaces?.length ?? 0;
  const practiceAmount = info?.legalPractice?.length ?? 0;
  const publicationsAmount = info?.journal_count ?? 0;

  if (props.isCompany) {
    return [
      { id: 1, label: 'Судебная практика', value: 'practice', amount: practiceAmount },
      { id: 2, label: 'Публикации', value: 'publications', amount: publicationsAmount },
    ];
  }

  return [
    { id: 1, label: 'Образование', value: 'education' },
    { id: 2, label: 'Места работы', value: 'jobs', amount: jobsAmount },
    { id: 3, label: 'Судебная практика', value: 'practice', amount: practiceAmount },
    { id: 4, label: 'Публикации', value: 'publications', amount: publicationsAmount },
  ];
});
const activeTab: Ref<string | number> = ref(tabList.value[0]?.value ?? 'practice');
</script>

<template>
  <section id="experience" :class="['lawyer-experience-section', { _mobile: isMobile }]">
    <h2 class="lawyer-experience-section__title">Опыт</h2>
    <AppTabs
      v-model="activeTab"
      class="lawyer-experience-section__tabs"
      :tab-list="tabList"
      :max-width="isCompany ? '340px' : '180px'"
    >
      <template #tab-extra="{ tab }">
        <span v-if="tab.amount !== undefined" class="app-tabs__amount _text">
          ({{ tab.amount }})
        </span>
      </template>
      <template #header>
        <div class="lawyer-experience-section__content">
          <EducationBlock v-if="activeTab === 'education'" />
          <JobsBlock v-else-if="activeTab === 'jobs'" />
          <ArbitragePracticeBlock v-else-if="activeTab === 'practice'" />
          <PublicationsBlock
            v-else-if="activeTab === 'publications'"
            :is-company="isCompany"
          />
        </div>
      </template>
    </AppTabs>
  </section>
</template>

<style lang="less">
.lawyer-experience-section {
  &__title {
    font-weight: 600;
    font-size: @size-x6;
    line-height: 25px;
    margin: 0 0 @size-x5;
  }

  .app-tabs._default .app-tabs__item {
    flex: 1 1 auto;
    font-size: 14px;
    font-weight: 600;
    line-height: 22px;
    z-index: 1;
  }

  .app-tabs._default .app-tabs__item._active {
    z-index: 2;
    border-bottom-color: #fff;
  }

  .app-tabs__item-label {
    padding: 10px @size-x5;
    gap: @main-size;
    align-items: baseline;
  }

  &__content {
    .box-shadow();

    position: relative;
    background: #fff;
    border-radius: 0 0 @main-size @main-size;
    padding: 30px;
  }

  &._mobile {
    .app-tabs__list {
      overflow-x: auto;
    }

    .app-tabs._default .app-tabs__item {
      font-size: 14px;
      line-height: 19px;
    }

    .app-tabs__item-label {
      padding: 10px;
    }
  }

  &._mobile &__title {
    margin-bottom: @size-x4;
  }

  &._mobile &__content {
    padding: @size-x4 @size-x4 @size-x7;
  }
}
</style>
